import React, { useState } from 'react'
import { Badge, Avatar, AvatarFallback, TwEmoji } from '@t-one-internal/design-system'
import { X } from '@phosphor-icons/react'
import { useAuth } from '../../../../context/AuthContextBackend'
import type { Employee, RecognitionType, CompanyValue } from '../../../../types/recognition'
import type { CompanyValueOption } from '../../../../api/recognitionApi'
import { recognitionTypes } from '../../mockData'
import { hashValueColor } from '../../utils/valueColors'
import AddCompanyValueModal from '../AddCompanyValueModal'

interface ValuePreviewStepProps {
  selectedValue: CompanyValue | null
  onSelectValue: (value: CompanyValue) => void
  selectedRecipients: Employee[]
  selectedType: RecognitionType | null
  message: string
  companyValueOptions?: CompanyValueOption[]
  onValueCreated?: (value: CompanyValueOption) => void
  onValueDeleted?: (valueId: string) => Promise<void>
}

const ValuePreviewStep: React.FC<ValuePreviewStepProps> = ({
  selectedValue,
  onSelectValue,
  selectedRecipients,
  selectedType,
  message,
  companyValueOptions = [],
  onValueCreated,
  onValueDeleted,
}) => {
  const { user } = useAuth()
  const [addValueOpen, setAddValueOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const senderInitials = user
    ? `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase() || 'Me'
    : 'Me'
  const senderName = user ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() : 'You'

  return (
    <div>
      <p className="text-sm font-semibold text-foreground mb-3">Tag a company value</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {companyValueOptions.map((v) => {
          const vc = hashValueColor(v.name)
          const isSelected = selectedValue === v.name
          const isDeleting = deletingId === v.id
          return (
            <div key={v.id} className="relative flex items-center">
              <button
                onClick={() => {
                  if (isSelected) {
                    onSelectValue(null as unknown as CompanyValue) // Allow deselecting
                  } else {
                    onSelectValue(v.name as CompanyValue)
                  }
                }}
                disabled={isDeleting}
                className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-all"
                style={{
                  color: isSelected ? vc.text : 'hsl(var(--muted-foreground))',
                  backgroundColor: isSelected ? vc.bg : 'transparent',
                  borderColor: isSelected ? vc.text : 'hsl(var(--border))',
                  opacity: isDeleting ? 0.5 : 1,
                }}
              >
                {v.name}
              </button>
              {!!user?.is_superadmin && (
                <button
                  onClick={async (e) => {
                    e.stopPropagation()
                    if (!onValueDeleted) return
                    setDeletingId(v.id)
                    try {
                      await onValueDeleted(v.id)
                    } finally {
                      setDeletingId(null)
                    }
                  }}
                  disabled={isDeleting}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/80 transition-colors"
                  title="Delete value"
                >
                  <X size={10} weight="bold" />
                </button>
              )}
            </div>
          )
        })}
        {!!user?.is_superadmin && (
          <>
            <button
              onClick={() => setAddValueOpen(true)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold border border-dashed text-muted-foreground hover:text-foreground hover:border-foreground transition-all flex items-center gap-1"
            >
              <span className="text-sm leading-none">+</span> Add New
            </button>
            <AddCompanyValueModal
              open={addValueOpen}
              onClose={() => setAddValueOpen(false)}
              onCreated={(v) => {
                onValueCreated?.(v)
                onSelectValue(v.name as CompanyValue)
              }}
            />
          </>
        )}
      </div>

      {/* Preview */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
          Preview
        </h4>
        <div className="bg-muted rounded-xl p-4 border">
          <div className="flex items-center gap-2.5 mb-2.5">
            <Avatar className="w-7 h-7">
              <AvatarFallback className="text-[10px] font-bold bg-primary/20 text-primary">
                {senderInitials}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs font-semibold text-foreground">{senderName}</span>
            <span className="text-xs text-muted-foreground">&rarr;</span>
            {selectedRecipients.length === 1 ? (
              <>
                <Avatar className="w-7 h-7">
                  <AvatarFallback className="text-[10px] font-bold bg-primary/20 text-primary">
                    {selectedRecipients[0].initials}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs font-semibold text-foreground">
                  {selectedRecipients[0].name}
                </span>
              </>
            ) : (
              <span className="text-xs font-semibold text-foreground">
                {selectedRecipients.length} people
              </span>
            )}
          </div>
          <p className="text-xs text-foreground leading-relaxed">
            {message || 'Your message will appear here...'}
          </p>
          <div className="flex gap-2 mt-2.5">
            {selectedType && (
              <Badge variant="info" className="text-[10px]">
                <TwEmoji emoji={recognitionTypes.find((t) => t.id === selectedType)?.icon || ''} size={14} />{' '}
                {recognitionTypes.find((t) => t.id === selectedType)?.label}
              </Badge>
            )}
            {selectedValue && (() => {
              const pvc = hashValueColor(selectedValue)
              return (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                  style={{
                    color: pvc.text,
                    backgroundColor: pvc.bg,
                  }}
                >
                  {selectedValue}
                </span>
              )
            })()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ValuePreviewStep
